import { Eye, History, LeafIcon, Edit, Trash2, UploadCloudIcon, ChevronLeft, ChevronRight, Receipt, Rewind } from "lucide-react";
import { ContainerCard, ModuleTitleCard, EmployeeDetails } from "../../components/ui/cards";
import { useCallback, useEffect, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { REIMBURSEMENTS_CONFIG, REIMBURSEMENT_URL } from "../../constants/api";
import { truncateText } from "../../components/common/CommonFunctions";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { DatePicker, Select, Upload as AntUpload, Image, TimePicker } from "antd";
import dayjs from 'dayjs';
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";

const ReimbursementRequest = () => {
    // Constants
    const FILE_TYPES = {
        IMAGE: "image/",
        PDF: "application/pdf"
    };
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    // states
    const companyid = sessionStorage.getItem("userId");
    const { fetch, create, update } = useApiMethods()
    const [formData, setFormData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)
    const [openDialog, setOpenDialog] = useState(false)
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showApprovalLog, setShowApprovalLog] = useState(false);
    const [dropdownData, setDropdownData] = useState({
        reimbursementTypes: []
    });
    const [screenLoader, setScreenLoader] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [cancelRecord, setCancelRecord] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [wholeResponse, setWholeResponse] = useState(null);

    // Mode of transport choices — must match backend REIMBURSEMENT_MODE_OF_TRANSPORT.
    const MODE_OF_TRANSPORT_OPTIONS = [
        { value: 'car', label: 'Car' },
        { value: 'bus', label: 'Bus' },
        { value: 'train', label: 'Train' },
        { value: 'flight', label: 'Flight' },
        { value: 'auto', label: 'Auto' },
        { value: 'taxi', label: 'Taxi' },
        { value: 'bike', label: 'Bike' },
        { value: 'own_vehicle', label: 'Own Vehicle' },
        { value: 'other', label: 'Other' },
    ];

    // Form states
    const [formState, setFormState] = useState({
        reimbursements_type: null,
        description: "",
        requested_amount: "",
        date: dayjs(),
        from_location: "",
        to_location: "",
        mode_of_transport: null,
        distance_km: "",
        uploaded_documents: [],
        existing_attachments: [], // For editing existing attachments
        company_id: companyid,
        is_active: true,
        request_type: "reimbursement request",
        is_draft: false,
    });

    // Resolve the selected type's category so we know whether to show travel fields.
    const selectedType = dropdownData.reimbursementTypes.find(
        (t) => t.id === formState.reimbursements_type
    );
    const isTravelType = selectedType?.expense_category === 'travel';
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Check if existing attachments can be deleted (when editing)
    const canDeleteExisting = editRecord;

    // Combine existing attachments and new uploads for carousel display
    const allAttachments = [
        ...(formState.existing_attachments?.map(url => ({ type: 'existing', url, isExisting: true, canDelete: canDeleteExisting })) || []),
        ...(formState.uploaded_documents?.map(file => ({ type: 'new', file, isExisting: false, canDelete: true })) || [])
    ];

    const getReimbursementType = useCallback(async () => {
        try {
            const apiData = await fetch(REIMBURSEMENTS_CONFIG, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const fetchDropdown = async () => {
        setScreenLoader(true)
        setLoading(true)

        const [reimbursementTypes] = await Promise.all([
            getReimbursementType()
        ]);

        setDropdownData({
            reimbursementTypes: reimbursementTypes || []
        });
        setLoading(false);
        setScreenLoader(false);
    }

    useEffect(() => {
        const initialize = async () => {
            await fetchDropdown();
        }

        initialize();
    }, []);

    const StatusBadge = ({ status }) => {
        const getStatusConfig = (status) => {
            const config = {
                pending: {
                    text: 'Pending',
                    className: 'bg-yellow-100 text-yellow-700 border-yellow-200'
                },
                'cancel inprogress': {
                    text: 'Cancelling',
                    className: 'bg-orange-100 text-orange-700 border-orange-200'
                },
                approved: {
                    text: 'Approved',
                    className: 'bg-sky-100 text-sky-700 border-sky-200'
                },
                rejected: {
                    text: 'Rejected',
                    className: 'bg-red-100 text-red-700 border-red-200'
                },
                cancelled: {
                    text: 'Cancelled',
                    className: 'bg-gray-100 text-gray-600 border-gray-200'
                },
                partial: {
                    text: 'Partial',
                    className: 'bg-amber-100 text-amber-700 border-amber-200'
                },
                processed: {
                    text: 'Processed',
                    className: 'bg-emerald-100 text-emerald-700 border-emerald-300'
                }
            };

            return config[status] || {
                text: status?.charAt(0).toUpperCase() + status?.slice(1) || 'Pending',
                className: 'bg-blue-100 text-blue-700 border-blue-200'
            };
        };

        const statusConfig = getStatusConfig(status);

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.className}`}>
                {statusConfig.text}
            </span>
        );
    };

    // Time Chip Component
    const TimeChip = ({ time, type = "start" }) => {
        if (!time || time === '-') return null;

        const chipClass = type === "start"
            ? "bg-blue-100 text-blue-700 border-blue-200"
            : "bg-orange-100 text-orange-700 border-orange-200";

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${chipClass}`}>
                {formatTimeToAMPM(time)}
            </span>
        );
    };

    // Format time to AM/PM
    const formatTimeToAMPM = (timeString) => {
        if (!timeString || timeString === '-') return '';

        try {
            // Handle different time formats
            let time = timeString;
            if (timeString.includes(' ')) {
                // If it's a full datetime string, extract time part
                time = timeString.split(' ')[1];
            }

            const [hours, minutes] = time.split(':');
            const hour = parseInt(hours);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const formattedHour = hour % 12 || 12;

            return `${formattedHour}:${minutes} ${ampm}`;
        } catch {
            return timeString;
        }
    };

    // Format request type for better display
    const formatRequestType = (type) => {
        const typeMap = {
            'work_from_home': 'Work From Home',
            'permission': 'Permission',
            'leave': 'Leave'
        };
        return typeMap[type] || type;
    };

    // Format date for display
    const formatDateDisplay = (dateString) => {
        if (!dateString) return '-';

        try {
            // Handle date strings in "DD-MM-YYYY" format
            if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
                const [day, month, year] = dateString.split('-');
                // Create date object with proper format (YYYY-MM-DD)
                const date = new Date(`${year}-${month}-${day}`);
                return date.toLocaleDateString('en-GB'); // This will show as DD/MM/YYYY
            }

            // Handle date-time strings like "02-11-2025 10:00:00 AM"
            if (dateString.includes(' ') && dateString.includes('-')) {
                const [datePart, timePart] = dateString.split(' ');
                const [day, month, year] = datePart.split('-');
                const date = new Date(`${year}-${month}-${day} ${timePart}`);
                return date.toLocaleDateString('en-GB'); // Returns DD/MM/YYYY
            }

            // For other date formats, try direct parsing
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString; // Return original if invalid
            }
            return date.toLocaleDateString('en-GB'); // Returns DD/MM/YYYY

        } catch {
            return dateString;
        }
    };

    // Check if request date has passed
    const isRequestDatePassed = (fromDate) => {
        if (!fromDate) return false;

        try {
            const requestDate = new Date(fromDate);
            const today = new Date();
            requestDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            return requestDate < today;
        } catch (error) {
            return false;
        }
    };

    // File validation and handling
    const validateFile = (file) => {
        const isImage = file.type?.startsWith(FILE_TYPES.IMAGE);
        const isPDF = file.type === FILE_TYPES.PDF;

        if (!isImage && !isPDF) {
            ToastMsg("error", "Only image and PDF files are allowed!");
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            ToastMsg("error", "File size must be less than 5MB!");
            return false;
        }

        return true;
    };

    const handleFileChange = useCallback((fileList) => {
        if (!fileList || fileList.length === 0) {
            setFormState(prev => ({ ...prev, uploaded_documents: [] }));
            setCurrentImageIndex(0);
            return;
        }

        const validFiles = fileList.filter(file => validateFile(file));

        setFormState(prev => {
            const existingFiles = prev.uploaded_documents || [];
            // Merge new files with existing, avoiding duplicates by uid
            const mergedFiles = [...existingFiles];
            validFiles.forEach(newFile => {
                const exists = mergedFiles.some(existing => existing.uid === newFile.uid);
                if (!exists) {
                    mergedFiles.push(newFile);
                }
            });
            return {
                ...prev,
                uploaded_documents: mergedFiles
            };
        });
        setCurrentImageIndex(0);
    }, []);

    // Handle form field changes
    const handleFormChange = (field, value) => {
        setFormState(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // columns
    const columns = [
        {
            Header: 'Reimbursement Type',
            accessor: 'reimbursements_type',
            filterable: true,
            sortable: true,
            width: '200px',
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300">
                    {value?.name || 'Not Mentioned'}
                </span>
            )
        },
        {
            Header: 'Description',
            accessor: 'description',
            sortable: true,
            filterable: true,
            width: '250px',
            Cell: ({ value }) => truncateText(value, 30, "300px", "balance")
        },
        {
            Header: 'Requested Amount',
            accessor: 'requested_amount',
            sortable: true,
            filterable: true,
            width: '150px',
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                    ₹{value || 0}
                </span>
            )
        },
        {
            Header: 'Approved Amount',
            accessor: 'approved_amount',
            sortable: true,
            width: '150px',
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                    ₹{value || 0}
                </span>
            )
        },
        {
            Header: 'Status',
            accessor: 'approval_status',
            sortable: true,
            filterable: true,
            filterType: 'requestStatus',
            Cell: ({ value }) => <StatusBadge status={value} />,
            width: '120px'
        },
        // {
        //     Header: 'Submitted Date',
        //     accessor: 'created_at',
        //     sortable: true,
        //     filterable: true,
        //     Cell: ({ value }) => (
        //         <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
        //             {formatDateDisplay(value)}
        //         </span>
        //     ),
        //     width: '150px'
        // },
        {
            Header: 'Actions',
            accessor: 'actions',
            sortable: false,
            fixed: true,
            align: 'right',
            width: '80px',
            Cell: ({ row }) => {
                const isDraftRow = Boolean(row.is_draft);
                const isPending = row.approval_status === 'pending';
                const canEditOrDelete = isDraftRow || isPending;

                return (
                    <div className="flex justify-end gap-2">
                        {canEditOrDelete ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setIsEdit(true);
                                        setEditRecord(row);
                                        setFormState({
                                            reimbursements_type: row.reimbursements_type?.id || row.reimbursements_type || '',
                                            description: row.description || '',
                                            requested_amount: row.requested_amount || '',
                                            date: row.date ? dayjs(row.date) : dayjs(),
                                            from_location: row.from_location || '',
                                            to_location: row.to_location || '',
                                            mode_of_transport: row.mode_of_transport || null,
                                            distance_km: row.distance_km ?? '',
                                            uploaded_documents: [], // New files being uploaded
                                            existing_attachments: row.approval_status !== 'approved' ? (row.attachments || []) : [], // Existing attachments if not approved
                                            company_id: parseInt(companyid),
                                            is_active: true,
                                            request_type: "reimbursement request",
                                            is_draft: Boolean(row.is_draft),
                                        });
                                        setCurrentImageIndex(0);
                                        setOpenDialog(true);
                                    }}
                                    className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:text-blue-700"
                                    title={isDraftRow ? "Edit Draft" : "Edit Request"}
                                >
                                    <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setDeleteRecord(row);
                                        setShowDeleteConfirm(true);
                                    }}
                                    className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700"
                                    title="Delete Request"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setSelectedRequest(row);
                                        setShowApprovalLog(true);
                                    }}
                                    disabled={!row.approval_log || row.approval_log.length === 0}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${!row.approval_log || row.approval_log.length === 0
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 hover:text-amber-700'
                                        }`}
                                    title={(!row.approval_log || row.approval_log.length === 0) ? "No approval log available" : "View Approval Log"}
                                >
                                    <History className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setCancelRecord(row);
                                        setCancelReason("");
                                        setShowCancelDialog(true);
                                    }}
                                    disabled={['rejected', 'cancel inprogress', 'cancelled', 'processed'].includes(row.approval_status)}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${['cancelled', 'cancel inprogress', 'rejected', 'processed'].includes(row.approval_status)
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 hover:text-orange-700'
                                        }`}
                                    title={row.approval_status === 'processed' ? 'Cannot cancel — already processed' : 'Cancel Request'}
                                >
                                    <Rewind className="h-3.5 w-3.5" />
                                </Button>
                            </>
                        )}
                    </div>
                )
            }
        }
    ];

    // Handle delete with confirmation
    const handleDeleteRequest = async () => {
        if (!deleteRecord) return;

        setLoading(true);
        try {
            const response = await update(REIMBURSEMENT_URL, {
                id: deleteRecord.id,
                is_active: false
            });

            if (response.status) {
                ToastMsg('success', 'Reimbursement request deleted successfully');
                setShowDeleteConfirm(false);
                setDeleteRecord(null);
                setServerTrigger(!serverTrigger); // Refresh the table
            } else {
                ToastMsg('error', response.message || 'Failed to delete request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while deleting the request');
            console.error('Delete error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel request
    const handleCancelRequest = async () => {
        if (!cancelRecord || !cancelReason.trim()) {
            ToastMsg('error', 'Please provide a cancel reason');
            return;
        }

        setLoading(true);
        try {
            const response = await update(REIMBURSEMENT_URL, {
                id: cancelRecord.id,
                cancel_reason: cancelReason,
                approval_status: 'cancel inprogress',
                request_type: "cancel request"
            });

            if (response?.status === "success") {
                ToastMsg('success', 'Request cancelled successfully');
                setShowCancelDialog(false);
                setCancelRecord(null);
                setCancelReason("");
                setServerTrigger(!serverTrigger);
            } else {
                ToastMsg('error', response.message || 'Failed to cancel request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while cancelling the request');
            console.error('Cancel error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmitRequest = async (isDraft = false) => {
        if (!isDraft) {
            if (!formState.reimbursements_type || !formState.description || !formState.requested_amount || !formState.date) {
                ToastMsg('error', 'Please fill in all required fields');
                return;
            }
            if (Number(formState.requested_amount) <= 0) {
                ToastMsg('error', 'Requested amount must be greater than 0');
                return;
            }

            if (isTravelType) {
                if (!formState.from_location?.trim() || !formState.to_location?.trim() || !formState.mode_of_transport) {
                    ToastMsg('error', 'From Location, To Location and Mode of Transport are required for travel reimbursements');
                    return;
                }
                const km = Number(formState.distance_km);
                if (!formState.distance_km || !Number.isFinite(km) || km <= 0) {
                    ToastMsg('error', 'Distance (KM) must be a positive number for travel reimbursements');
                    return;
                }
            }
        } else if (!formState.reimbursements_type) {
            ToastMsg('error', 'Please select a reimbursement type before saving a draft');
            return;
        }

        setLoading(true);

        try {
            const dataForm = new FormData();
            dataForm.append("reimbursements_type", formState.reimbursements_type || "")
            dataForm.append("description", formState.description || "")
            dataForm.append("requested_amount", formState.requested_amount ? +formState.requested_amount : 0)
            dataForm.append("date", dayjs(formState.date).format('YYYY-MM-DD'))
            if (isTravelType) {
                dataForm.append("from_location", formState.from_location || "")
                dataForm.append("to_location", formState.to_location || "")
                dataForm.append("mode_of_transport", formState.mode_of_transport || "")
                dataForm.append("distance_km", formState.distance_km || "")
            }

            // Append existing attachments (URLs) first
            if (formState.existing_attachments?.length > 0) {
                formState.existing_attachments.forEach((url, index) => {
                    dataForm.append(`uploaded_documents_${index + 1}`, url);
                });
            }

            // Append new uploaded files
            if (formState.uploaded_documents?.length > 0) {
                const startIndex = formState.existing_attachments?.length || 0;
                formState.uploaded_documents.forEach((file, index) => {
                    dataForm.append(`uploaded_documents_${startIndex + index + 1}`, file.originFileObj || file);
                });
            }

            dataForm.append("is_active", formState.is_active || true)
            dataForm.append("company_id", formState.company_id || "")
            dataForm.append("request_type", formState.request_type || "reimbursement request")
            dataForm.append("is_draft", isDraft ? "true" : "false");

            if (isEdit && editRecord?.id) {
                dataForm.append("id", editRecord.id);
            }

            let response

            if (isEdit) {
                response = await update(REIMBURSEMENT_URL, dataForm, true);
            } else {
                response = await create(REIMBURSEMENT_URL, dataForm, true);
            }

            if (response.status !== 'fail') {
                ToastMsg(
                    'success',
                    isDraft
                        ? (response.message || 'Draft saved successfully')
                        : (isEdit ? 'Reimbursement request updated successfully' : 'Reimbursement request submitted successfully')
                );
                setOpenDialog(false);
                setIsEdit(false);
                setEditRecord(null);
                // Reset form state
                setFormState({
                    reimbursements_type: null,
                    description: "",
                    requested_amount: "",
                    date: dayjs(),
                    from_location: "",
                    to_location: "",
                    mode_of_transport: null,
                    distance_km: "",
                    uploaded_documents: [],
                    company_id: companyid,
                    is_active: true,
                    request_type: "reimbursement request",
                    is_draft: false,
                });
                setCurrentImageIndex(0);
                setServerTrigger(!serverTrigger); // Refresh the table
            } else {
                ToastMsg('error', response.message || 'Failed to submit request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while submitting the request');
            console.error('Submission error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Reset form function
    const resetForm = () => {
        setIsEdit(false);
        setEditRecord(null);
        setFormState({
            reimbursements_type: null,
            description: "",
            requested_amount: "",
            date: dayjs(),
            from_location: "",
            to_location: "",
            mode_of_transport: null,
            distance_km: "",
            uploaded_documents: [],
            existing_attachments: [],
            company_id: companyid,
            is_active: true,
            request_type: "reimbursement request",
            is_draft: false,
        });
        setCurrentImageIndex(0);
    };

    const statusStyles = {
        pending: {
            bg: "bg-amber-50 dark:bg-amber-950/20",
            border: "border-amber-200",
            text: "text-amber-700 dark:text-amber-400",
        },
        approved: {
            bg: "bg-emerald-50 dark:bg-emerald-950/20",
            border: "border-emerald-200",
            text: "text-emerald-700 dark:text-emerald-400",
        },
        rejected: {
            bg: "bg-red-50 dark:bg-red-950/20",
            border: "border-red-200",
            text: "text-red-700 dark:text-red-400",
        },
        processed: {
            bg: "bg-blue-50 dark:bg-blue-950/20",
            border: "border-blue-200",
            text: "text-blue-700 dark:text-blue-400",
        },
        partial: {
            bg: "bg-orange-50 dark:bg-orange-950/20",
            border: "border-orange-200",
            text: "text-orange-700 dark:text-orange-400",
        },
        cancelled: {
            bg: "bg-slate-50 dark:bg-slate-900/20",
            border: "border-slate-200",
            text: "text-slate-700 dark:text-slate-400",
        },
        all: {
            bg: "bg-violet-50 dark:bg-violet-950/20",
            border: "border-violet-200",
            text: "text-violet-700 dark:text-violet-400",
        },
    };

    return (
        <ContainerCard>
            {/* title */}
            <ModuleTitleCard
                Icon={Receipt}
                title="Reimbursement Request"
                subtitle="Raise a request for your incurred reimbursements"
                totalRecords={totalRecords}
                buttonEnable
                setOpenDialog={setOpenDialog}
                buttonName="Add Reimbursement Request"
            >
            </ModuleTitleCard>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mt-2 mb-2">
                {Object.entries(wholeResponse?.data?.summary || {}).map(([key, value]) => {
                    const style = statusStyles[key];
                    return (
                        <div
                            key={key}
                            className={`${style.bg} dark:bg-slate-900 border ${style.border} rounded-xl p-2.5 shadow-sm hover:shadow-md transition-all`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h4 className={`text-xs font-medium uppercase tracking-wide ${style.text}`}>
                                    {key}
                                </h4>

                                <span className={`text-lg font-bold ${style.text}`}>
                                    {value.count}
                                </span>
                            </div>

                            <div className="border-t pt-2">
                                {key === "all" ? (
                                    <>
                                        <div className="flex justify-between text-xs">
                                            <span>Requested</span>
                                            <span className="font-semibold">
                                                ₹{value.requested_amount?.toLocaleString()}
                                            </span>
                                        </div>

                                        <div className="flex justify-between text-xs mt-1 text-green-600">
                                            <span>Approved</span>
                                            <span className="font-semibold">
                                                ₹{value.approved_amount?.toLocaleString()}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex justify-between text-xs">
                                        <span>Amount</span>
                                        <span className={`font-semibold ${style.text}`}>
                                            ₹{value.amount?.toLocaleString()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Data Table */}
            <Reactable
                columns={columns}
                data={formData}
                setData={setFormData}
                setTotalCount={setTotalRecords}
                serverSide
                url={REIMBURSEMENT_URL}
                serverTrigger={serverTrigger}
                rowSize={"md"}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={true}
                setWholeResponse={setWholeResponse}
            />

            {/* Approval Log Dialog */}
            <DialogModal
                open={showApprovalLog}
                onClose={() => setShowApprovalLog(false)}
                title="Approval Log"
                showActions={false}
                width="800px"
            >
                {selectedRequest && selectedRequest.approval_log && selectedRequest.approval_log.length > 0 ? (
                    <div className="space-y-4">
                        <div className="overflow-x-auto h-60 relative">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                                            Date & Time
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                                            Approver
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                                            Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {selectedRequest.approval_log.map((log, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {new Date(log.approved_date_and_time).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {log.approver_name || '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <StatusBadge status={log.approval_status} />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {log.approval_notes || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-sm flex items-center justify-between gap-2 text-gray-600 border-t pt-4">
                            <div>
                                {(() => {
                                    const record = selectedRequest;
                                    let empDetails = {
                                        ...record?.employee,
                                        designation_id: record?.employee?.designation
                                            ? {
                                                id: record?.employee.designation.id,
                                                designation_name: record?.employee.designation.name,
                                            }
                                            : { id: 0, designation_name: "" },
                                        first_name: record?.employee?.employee_name || "-",
                                        id: record?.employee?.employee_id || "-",
                                    };
                                    return <EmployeeDetails details={empDetails} size="xs" />;
                                })()}
                            </div>
                            <p><strong>Request Type:</strong> {formatRequestType(selectedRequest.leave_request_type)}</p>
                            <p><strong>Current Status:</strong> <StatusBadge status={selectedRequest.approval_status} /></p>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No approval log available for this request.</p>
                    </div>
                )}
            </DialogModal>

            {/* Delete Confirmation Dialog */}
            <DialogModal
                open={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setDeleteRecord(null);
                }}
                onSave={handleDeleteRequest}
                title="Delete Reimbursement Request"
                saveLabel="Delete"
                width="400px"
                loading={loading}
                saveButtonColor="destructive"
            >
                <div className="text-center py-4">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                        <Trash2 className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Delete Reimbursement Request
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this reimbursement request? This action cannot be undone.
                    </p>
                    {deleteRecord && (
                        <div className="mt-4 p-3 flex flex-col gap-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>Type:</strong> {deleteRecord.reimbursements_type?.name || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>Amount:</strong> ₹{deleteRecord.requested_amount || 0}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>Status:</strong> <StatusBadge status={deleteRecord.approval_status} />
                            </p>
                        </div>
                    )}
                </div>
            </DialogModal>

            {/* Add Reimbursement Request Dialog - UPDATED TO USE NEW FORM STATE NAMES */}
            <DialogModal
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    resetForm();
                }}
                onSave={() => handleSubmitRequest(false)}
                onDraft={(isEdit && !editRecord?.is_draft) ? undefined : () => handleSubmitRequest(true)}
                title={isEdit ? (editRecord?.is_draft ? "Edit Draft Request" : "Edit Reimbursement Request") : "Apply Reimbursement Request"}
                saveLabel={isEdit && !editRecord?.is_draft ? "Update Request" : "Submit Request"}
                draftLabel="Save Draft"
                width="700px"
                loading={loading}
            >
                <div className={cn("grid gap-8 grid-cols-1 md:grid-cols-2 p-2", "max-h-[65vh] overflow-y-auto pr-2")}>

                    {/* Left Column */}
                    <div className="space-y-4">
                        {/* Reimbursement Type */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Reimbursement Type <span className="text-red-500">*</span>
                            </label>
                            <Select
                                value={formState.reimbursements_type}
                                onChange={(value) => handleFormChange('reimbursements_type', value)}
                                placeholder="Select reimbursement type"
                                className="w-full"
                                size="large"
                            >
                                {dropdownData.reimbursementTypes.map((reimb) => (
                                    <Select.Option key={reimb.id} value={reimb.id}>
                                        {reimb.reimbursement_type_name}
                                        {reimb.expense_category === 'travel' && (
                                            <span className="ml-2 text-[10px] text-blue-600 dark:text-blue-300">(travel)</span>
                                        )}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>

                        {/* Date */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Date <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                value={formState.date}
                                onChange={(value) => handleFormChange('date', value)}
                                className="w-full"
                                size="large"
                                format="YYYY-MM-DD"
                                placeholder="Select date"
                                disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                            />
                        </div>

                        {/* Amount */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Amount <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={formState.requested_amount}
                                onChange={(e) => handleFormChange('requested_amount', e.target.value)}
                                placeholder="Enter amount"
                                className="w-full"
                                min={0}
                            />
                        </div>



                        {/* Reason */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <Textarea
                                value={formState.description}
                                onChange={(e) => handleFormChange('description', e.target.value)}
                                placeholder="Please provide a description for your reimbursement..."
                                rows={5}
                                showCharCount={true}
                                maxLength={500}
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">

                        {isTravelType && (
                            <>
                                {/* From Location */}
                                <div className="space-y-2">
                                    <label className={modalFieldLabelClass}>
                                        From Location <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        value={formState.from_location}
                                        onChange={(e) => handleFormChange('from_location', e.target.value)}
                                        placeholder="e.g. Chennai"
                                        className="w-full"
                                    />
                                </div>

                                {/* To Location */}
                                <div className="space-y-2">
                                    <label className={modalFieldLabelClass}>
                                        To Location <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        value={formState.to_location}
                                        onChange={(e) => handleFormChange('to_location', e.target.value)}
                                        placeholder="e.g. Bangalore"
                                        className="w-full"
                                    />
                                </div>

                                {/* Mode of Transport */}
                                <div className="space-y-2">
                                    <label className={modalFieldLabelClass}>
                                        Mode of Transport <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        value={formState.mode_of_transport}
                                        onChange={(value) => handleFormChange('mode_of_transport', value)}
                                        placeholder="Select mode of transport"
                                        className="w-full"
                                        size="large"
                                        options={MODE_OF_TRANSPORT_OPTIONS}
                                    />
                                </div>

                                {/* Distance (KM) */}
                                <div className="space-y-2">
                                    <label className={modalFieldLabelClass}>
                                        Distance (KM) <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        value={formState.distance_km}
                                        onChange={(e) => handleFormChange('distance_km', e.target.value)}
                                        placeholder="e.g. 350"
                                        className="w-full"
                                    />
                                </div>
                            </>
                        )}

                        {/* File Upload */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Attachment
                            </label>
                            <div>
                                <AntUpload.Dragger
                                    accept="image/*,.pdf"
                                    multiple
                                    maxCount={10}
                                    beforeUpload={() => false}
                                    onChange={({ fileList }) => handleFileChange(fileList)}
                                    showUploadList={false}
                                    className="dark:bg-gray-800 dark:border-gray-700"
                                >
                                    <UploadCloudIcon className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                        Click to upload document
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                        Supported formats: .jpg, .png, .pdf (Max: 5MB)
                                    </p>
                                </AntUpload.Dragger>

                                {/* File Preview */}
                                {allAttachments && allAttachments.length > 0 && (
                                    <div className="mt-2">
                                        {allAttachments.length === 1 ? (
                                            // SINGLE FILE PREVIEW
                                            <>
                                                {allAttachments[0].isExisting ? (
                                                    // Existing attachment (URL)
                                                    <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                        {allAttachments[0].url?.toLowerCase().includes('.pdf') ? (
                                                            <a
                                                                href={allAttachments[0].url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-sidebar underline underline-offset-2 text-sm flex items-center gap-2"
                                                            >
                                                                Preview PDF <Eye className="w-3 h-3" />
                                                            </a>
                                                        ) : (
                                                            <Image
                                                                src={allAttachments[0].url}
                                                                alt="existing attachment"
                                                                width={120}
                                                                className="rounded-md shadow-sm"
                                                            />
                                                        )}
                                                        <span className="text-xs text-green-600 dark:text-green-400">Existing</span>
                                                        {allAttachments[0].canDelete && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => {
                                                                    const currentUrl = allAttachments[0].url;
                                                                    const updatedExisting = formState.existing_attachments.filter(url => url !== currentUrl);
                                                                    handleFormChange('existing_attachments', updatedExisting);
                                                                }}
                                                                className="text-red-600 p-2"
                                                                title="Remove existing file"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    // New uploaded file - Individual delete
                                                    <>
                                                        {allAttachments[0].file.type?.startsWith("image/") ? (
                                                            <div className="relative p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                                <Image
                                                                    src={URL.createObjectURL(allAttachments[0].file.originFileObj || allAttachments[0].file)}
                                                                    alt="preview"
                                                                    width={120}
                                                                    className="rounded-md shadow-sm"
                                                                />
                                                                <button
                                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-sm"
                                                                    title="Remove file"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ) : allAttachments[0].file.type === "application/pdf" ? (
                                                            <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                                <a
                                                                    href={URL.createObjectURL(allAttachments[0].file.originFileObj || allAttachments[0].file)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-sidebar underline underline-offset-2 text-sm flex items-center gap-2"
                                                                >
                                                                    Preview PDF <Eye className="w-3 h-3" />
                                                                </a>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                                    className="text-red-600 p-2"
                                                                    title="Remove file"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                                                    {allAttachments[0].file.name}
                                                                </span>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                                    className="text-red-600 p-2"
                                                                    title="Remove file"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            // MULTIPLE FILES CAROUSEL
                                            <div className="space-y-4">
                                                {/* Main Preview */}
                                                <div className="relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                                    <div className="flex justify-center items-center min-h-[150px] p-6">
                                                        {allAttachments[currentImageIndex].isExisting ? (
                                                            // Existing attachment (URL)
                                                            <>
                                                                {allAttachments[currentImageIndex].url?.toLowerCase().includes('.pdf') ? (
                                                                    <div className="text-center max-h-[100px]">
                                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">PDF Document</p>
                                                                        <a
                                                                            href={allAttachments[currentImageIndex].url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-700 text-white text-sm rounded-md"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                            View PDF
                                                                        </a>
                                                                    </div>
                                                                ) : (
                                                                    <Image
                                                                        src={allAttachments[currentImageIndex].url}
                                                                        alt={`existing ${currentImageIndex + 1}`}
                                                                        className="max-w-full max-h-[100px] object-contain rounded"
                                                                    />
                                                                )}
                                                            </>
                                                        ) : (
                                                            // New uploaded file
                                                            <>
                                                                {allAttachments[currentImageIndex].file.type?.startsWith("image/") ? (
                                                                    <Image
                                                                        src={URL.createObjectURL(allAttachments[currentImageIndex].file.originFileObj || allAttachments[currentImageIndex].file)}
                                                                        alt={`preview ${currentImageIndex + 1}`}
                                                                        className="max-w-full max-h-[100px] object-contain rounded"
                                                                    />
                                                                ) : allAttachments[currentImageIndex].file.type === "application/pdf" ? (
                                                                    <div className="text-center max-h-[100px]">
                                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">PDF Document</p>
                                                                        <a
                                                                            href={URL.createObjectURL(allAttachments[currentImageIndex].file.originFileObj || allAttachments[currentImageIndex].file)}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-700 text-white text-sm rounded-md"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                            View PDF
                                                                        </a>
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-center">
                                                                        <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                                                                            <UploadCloudIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                                                                        </div>
                                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                            {allAttachments[currentImageIndex].file.name}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* Navigation Arrows */}
                                                    <button
                                                        onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : allAttachments.length - 1)}
                                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full flex items-center justify-center"
                                                    >
                                                        <ChevronLeft className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setCurrentImageIndex(prev => prev < allAttachments.length - 1 ? prev + 1 : 0)}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full flex items-center justify-center"
                                                    >
                                                        <ChevronRight className="w-4 h-4" />
                                                    </button>

                                                    {/* File Counter */}
                                                    <div className="absolute top-2 right-2 bg-gray-900 text-white px-2 py-1 rounded text-xs">
                                                        {currentImageIndex + 1} / {allAttachments.length}
                                                    </div>

                                                    {/* Existing File Badge */}
                                                    {allAttachments[currentImageIndex].isExisting && (
                                                        <div className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-xs">
                                                            Existing
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Thumbnails */}
                                                <div className="flex gap-2 overflow-x-auto pb-2">
                                                    {allAttachments.map((attachment, index) => (
                                                        <div key={attachment.isExisting ? attachment.url : attachment.file.uid || index} className="relative flex-shrink-0 group">
                                                            <button
                                                                onClick={() => setCurrentImageIndex(index)}
                                                                className={`relative w-12 h-12 rounded border-2 overflow-hidden ${index === currentImageIndex
                                                                    ? 'border-blue-500'
                                                                    : 'border-gray-300 dark:border-gray-600'
                                                                    }`}
                                                            >
                                                                {attachment.isExisting ? (
                                                                    // Existing attachment thumbnail
                                                                    <>
                                                                        {attachment.url?.toLowerCase().includes('.pdf') ? (
                                                                            <div className="w-full h-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                                                                <span className="text-xs text-red-600 dark:text-red-400">PDF</span>
                                                                            </div>
                                                                        ) : (
                                                                            <Image
                                                                                src={attachment.url}
                                                                                alt={`existing thumb ${index + 1}`}
                                                                                className="w-full h-full object-cover"
                                                                            />
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    // New file thumbnail
                                                                    <>
                                                                        {attachment.file.type?.startsWith("image/") ? (
                                                                            <Image
                                                                                src={URL.createObjectURL(attachment.file.originFileObj || attachment.file)}
                                                                                alt={`thumb ${index + 1}`}
                                                                                className="w-full h-full object-cover"
                                                                            />
                                                                        ) : attachment.file.type === "application/pdf" ? (
                                                                            <div className="w-full h-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                                                                <span className="text-xs text-red-600 dark:text-red-400">PDF</span>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                                                                <span className="text-xs text-gray-600 dark:text-gray-400">FILE</span>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {/* Existing file indicator */}
                                                                {attachment.isExisting && (
                                                                    <div className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border border-white"></div>
                                                                )}
                                                            </button>

                                                            {/* Individual delete button for deletable files */}
                                                            {attachment.canDelete && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (attachment.isExisting) {
                                                                            // Remove from existing attachments
                                                                            const currentUrl = attachment.url;
                                                                            const updatedExisting = formState.existing_attachments.filter(url => url !== currentUrl);
                                                                            handleFormChange('existing_attachments', updatedExisting);
                                                                        } else {
                                                                            // Remove from new uploads
                                                                            const currentFile = attachment.file;
                                                                            const updatedFiles = formState.uploaded_documents.filter(file =>
                                                                                file.uid !== currentFile.uid
                                                                            );
                                                                            handleFormChange('uploaded_documents', updatedFiles);
                                                                        }
                                                                        // Adjust current index if necessary
                                                                        const newTotalAttachments = (attachment.isExisting ?
                                                                            formState.existing_attachments.length - 1 :
                                                                            formState.existing_attachments.length) +
                                                                            (attachment.isExisting ?
                                                                                formState.uploaded_documents.length :
                                                                                formState.uploaded_documents.length - 1);
                                                                        if (index === currentImageIndex && index >= newTotalAttachments) {
                                                                            setCurrentImageIndex(Math.max(0, index - 1));
                                                                        } else if (index < currentImageIndex) {
                                                                            setCurrentImageIndex(currentImageIndex - 1);
                                                                        }
                                                                    }}
                                                                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    title="Remove this file"
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* File Info and Actions */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                                            {allAttachments[currentImageIndex].isExisting
                                                                ? 'Existing file'
                                                                : truncateText(allAttachments[currentImageIndex].file.name, 20, "200px", "balance")
                                                            }
                                                        </span>
                                                        {allAttachments[currentImageIndex].isExisting && !allAttachments[currentImageIndex].canDelete && (
                                                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                                                                Cannot remove existing files
                                                            </span>
                                                        )}
                                                    </div>
                                                    {allAttachments[currentImageIndex].canDelete && (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => {
                                                                    if (allAttachments[currentImageIndex].isExisting) {
                                                                        // Remove from existing attachments
                                                                        const currentUrl = allAttachments[currentImageIndex].url;
                                                                        const updatedExisting = formState.existing_attachments.filter(url => url !== currentUrl);
                                                                        handleFormChange('existing_attachments', updatedExisting);
                                                                    } else {
                                                                        // Remove from new uploads
                                                                        const currentFile = allAttachments[currentImageIndex].file;
                                                                        const updatedFiles = formState.uploaded_documents.filter(file =>
                                                                            file.uid !== currentFile.uid
                                                                        );
                                                                        handleFormChange('uploaded_documents', updatedFiles);
                                                                    }
                                                                    // Adjust current index if necessary
                                                                    const newTotalAttachments = (allAttachments[currentImageIndex].isExisting ?
                                                                        formState.existing_attachments.length - 1 :
                                                                        formState.existing_attachments.length) +
                                                                        (allAttachments[currentImageIndex].isExisting ?
                                                                            formState.uploaded_documents.length :
                                                                            formState.uploaded_documents.length - 1);
                                                                    if (currentImageIndex >= newTotalAttachments && currentImageIndex > 0) {
                                                                        setCurrentImageIndex(currentImageIndex - 1);
                                                                    }
                                                                }}
                                                                className="text-red-600 p-2"
                                                                title="Remove this file"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                            {formState.uploaded_documents.length > 1 && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                                    className="text-red-600 p-2"
                                                                    title="Remove all new files"
                                                                >
                                                                    <div className="relative">
                                                                        <Trash2 className="w-4 h-4" />
                                                                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full flex items-center justify-center">
                                                                            <span className="text-xs text-white font-bold">+</span>
                                                                        </div>
                                                                    </div>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </DialogModal>

            {/* Cancel Request Dialog */}
            <DialogModal
                open={showCancelDialog}
                onClose={() => {
                    setShowCancelDialog(false);
                    setCancelRecord(null);
                    setCancelReason("");
                }}
                onSave={handleCancelRequest}
                title="Cancel Reimbursement Request"
                saveLabel="Cancel Request"
                width="500px"
                loading={loading}
                saveButtonColor="destructive"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Please provide a reason for cancelling this reimbursement request.
                    </p>
                    <Textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Enter cancel reason..."
                        rows={4}
                        showCharCount={true}
                        maxLength={500}
                        className="w-full"
                    />
                    {cancelRecord && (
                        <div className="p-3 flex justify-between items-center bg-gray-50 dark:bg-gray-800 rounded-md">
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>Type:</strong> {cancelRecord.reimbursements_type?.name || 'N/A'}
                            </p>
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>Amount:</strong> ₹{cancelRecord.requested_amount || 0}
                            </p>
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>Status:</strong> <StatusBadge status={cancelRecord.approval_status} />
                            </p>
                        </div>
                    )}
                </div>
            </DialogModal>

        </ContainerCard>
    );
}

export default ReimbursementRequest;